/**
 * Cart Drawer Enhancements
 * - Free shipping progress bar animation
 * - Cart count badge updates
 * - Dynamic progress bar recalculation on cart changes
 * - Checkout-button price mirror (Pipeline's theme.js only updates the first
 *   [data-cart-final]; we sync the duplicate inside the checkout button)
 */
(function() {
  'use strict';

  /* ── Currency formatting (mirrors formatMoneyClean in trieye-product.js) ── */
  function formatMoney(cents) {
    var amount = Number(cents);
    var decimal = (amount / 100).toFixed(2);
    var noDecimals = Math.round(amount / 100).toString();
    var parts = decimal.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    var withComma = parts[0] + ',' + parts[1];
    var noDecimalsComma = noDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    if (typeof theme !== 'undefined' && theme.moneyFormat && typeof theme.moneyFormat === 'string') {
      return theme.moneyFormat
        .replace('{{amount_with_comma_separator}}', withComma)
        .replace('{{amount_no_decimals_with_comma_separator}}', noDecimalsComma)
        .replace('{{amount_no_decimals}}', noDecimals)
        .replace('{{amount}}', decimal)
        .replace(/<[^>]*>/g, '');
    }
    return withComma;
  }

  /* ── Progress bar update ── */
  function updateProgressBar(cartTotal) {
    var bar = document.querySelector('[data-shipping-progress]');
    if (!bar) return;

    var limit = parseInt(bar.dataset.limit, 10) || 0;
    if (limit <= 0) return;

    var limitCents = limit * 100;
    var leftCents = Math.max(0, limitCents - cartTotal);
    var percent = Math.min(100, Math.round((cartTotal / limitCents) * 100));

    var fill = bar.querySelector('[data-progress-fill]');
    var msg = bar.querySelector('[data-progress-message]');
    if (!fill || !msg) return;

    // Animate fill width
    fill.style.width = percent + '%';
    // v77: toggle container class so CSS can hide the track when qualified
    bar.classList.toggle('shipping-progress--qualified', cartTotal >= limitCents);

    if (cartTotal >= limitCents) {
      fill.classList.add('shipping-progress__bar-fill--qualified');
      msg.innerHTML = '<span class="shipping-progress__icon">&#10003;</span> ' +
        (window.__cartStrings && window.__cartStrings.qualified
          ? window.__cartStrings.qualified
          : 'Your order qualifies for free shipping!');
    } else if (cartTotal === 0) {
      fill.classList.remove('shipping-progress__bar-fill--qualified');
      msg.innerHTML = window.__cartStrings && window.__cartStrings.baseline
        ? window.__cartStrings.baseline
        : 'Free shipping on orders over $' + limit;
    } else {
      fill.classList.remove('shipping-progress__bar-fill--qualified');
      var leftFormatted = '<strong>' + formatMoney(leftCents) + '</strong>';
      msg.innerHTML = (window.__cartStrings && window.__cartStrings.away
          ? window.__cartStrings.away
          : "You're ||amount|| away from FREE shipping!")
        .replace('||amount||', leftFormatted);
    }
  }

  /* ── Cart count badge ── */
  function updateCartCount(count) {
    document.querySelectorAll('[data-header-cart-count]').forEach(function(el) {
      el.textContent = count;
    });
    // Also update drawer title count
    var titleCount = document.querySelector('[data-cart-drawer-count]');
    if (titleCount) {
      titleCount.textContent = count > 0 ? '(' + count + ')' : '';
    }
  }

  /* ── Listen for cart changes ── */
  function onCartChange(cart) {
    if (!cart) return;
    // Use items_subtotal_price (pre-discount) for shipping qualification — a
    // 10% off code shouldn't disqualify someone who's already buying enough
    // to qualify on merchandise alone. Falls back to total_price if items_-
    // subtotal_price isn't present (older cart payloads).
    var qualifyAmount = cart.items_subtotal_price;
    if (qualifyAmount == null) qualifyAmount = cart.total_price;
    updateProgressBar(qualifyAmount || 0);
    updateCartCount(cart.item_count || 0);
  }

  // Hook into theme's cart events
  document.addEventListener('theme:cart:change', function(e) {
    if (e.detail && e.detail.cart) {
      onCartChange(e.detail.cart);
    } else {
      // Fallback: fetch cart data
      fetch('/cart.js')
        .then(function(r) { return r.json(); })
        .then(onCartChange)
        .catch(function() {});
    }
  });

  // Also update on cart reload (Pipeline theme event)
  document.addEventListener('theme:cart:reload', function() {
    setTimeout(function() {
      fetch('/cart.js')
        .then(function(r) { return r.json(); })
        .then(onCartChange)
        .catch(function() {});
    }, 600);
  });

  // Update when drawer becomes visible
  var observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(m) {
      if (m.target.classList && m.target.classList.contains('is-visible')) {
        fetch('/cart.js')
          .then(function(r) { return r.json(); })
          .then(onCartChange)
          .catch(function() {});
      }
    });
  });

  var drawerEl = document.querySelector('[data-drawer="drawer-cart"]');
  if (drawerEl) {
    observer.observe(drawerEl, { attributes: true, attributeFilter: ['class'] });
  }

  /* ── Checkout-button price mirror ──
     2026-05-19 (v71): observe scope WIDENED from [data-cart-bottom] to
     the entire #drawer-cart. v38 moved the subtotal row OUT of the
     sticky footer into the scrollable body, leaving only ONE
     [data-cart-final] span inside [data-cart-bottom] (the one in the
     CHECKOUT button). The old mirror gated on `spans.length >= 2`, so
     it returned early and never synced — CHECKOUT showed a stale per-
     line price (e.g. "$4.50") instead of the cart total.

     Pipeline's theme.js updates `this.finalPrice` = the FIRST
     [data-cart-final] in its container, which is now the subtotal span
     in the scrollable body. We mirror that source-of-truth text to all
     other [data-cart-final] spans in the drawer, including the one
     inside the CHECKOUT button. */
  function setupCartFinalMirror() {
    var drawer = document.getElementById('drawer-cart');
    if (!drawer) return;
    function sync() {
      var spans = drawer.querySelectorAll('[data-cart-final]');
      if (spans.length < 2) return;
      // The first span is updated by Pipeline; mirror its text into the rest.
      var sourceText = spans[0].textContent;
      for (var i = 1; i < spans.length; i++) {
        if (spans[i].textContent !== sourceText) {
          spans[i].textContent = sourceText;
        }
      }
    }
    sync();
    new MutationObserver(sync).observe(drawer, {
      characterData: true,
      childList: true,
      subtree: true
    });
  }

  /* ── 2026-05-19 (v56) — Checkout-button speculative warmup ──
     User reported CHECKOUT button felt slow to react. The submit
     itself is instant locally — the wait is the network round-trip
     to Shopify's /cart endpoint (which redirects to /checkout) +
     the /checkout page load.

     Two-stage warmup:
       1. <link rel="preconnect" href="checkout.shopify.com"> in
          theme.liquid (always-on DNS/TLS handshake).
       2. When the user TOUCHES the CHECKOUT button (touchstart
          fires ~100-200ms before click on mobile), we inject a
          `<link rel="prefetch">` for /cart. This re-primes the
          connection just-in-time, and on browsers that prefetch
          aggressively the cart-page response is in cache by the
          time the actual submit fires.

     Why touchstart and not click: touchstart fires on the FIRST
     finger contact; the user's finger is still down. Hover would
     work on desktop but doesn't exist on touch. Both browsers
     trigger touchstart on mobile, so we catch every tap. */
  function warmCheckout() {
    if (document.querySelector('link[data-checkout-warmup]')) return;
    var l = document.createElement('link');
    l.rel = 'prefetch';
    l.href = '/cart';
    l.setAttribute('data-checkout-warmup', '');
    document.head.appendChild(l);
  }
  function attachWarmup() {
    document.querySelectorAll('.cart-drawer-footer__checkout, .checkout__button')
      .forEach(function (btn) {
        btn.addEventListener('touchstart', warmCheckout, { once: true, passive: true });
        btn.addEventListener('mouseenter', warmCheckout, { once: true });
      });
  }

  /* ── 2026-05-19 (v64) — Generic link-warmup for product / collection
     pages, fallback for browsers without Speculation Rules support.
     Chrome / Edge use the <script type="speculationrules"> in
     theme.liquid (full prerender). Safari + older Chrome fall back
     here: when the user touches or hovers a /products/* or /collections/*
     link, we inject <link rel="prefetch"> to download the HTML in
     background. Native browser dedup keeps duplicates harmless. */
  function isWarmableLink(a) {
    if (!a || a.tagName !== 'A') return false;
    if (a.hasAttribute('data-no-prerender')) return false;
    var href = a.getAttribute('href') || '';
    return href.indexOf('/products/') === 0 || href.indexOf('/collections/') === 0;
  }
  function warmLink(href) {
    if (!href) return;
    if (document.querySelector('link[rel="prefetch"][href="' + href + '"]')) return;
    var l = document.createElement('link');
    l.rel = 'prefetch';
    l.href = href;
    document.head.appendChild(l);
  }
  function onPossibleWarmup(e) {
    var a = e.target && e.target.closest ? e.target.closest('a') : null;
    if (!a || !isWarmableLink(a)) return;
    warmLink(a.getAttribute('href'));
  }
  function attachLinkWarmup() {
    document.addEventListener('touchstart', onPossibleWarmup, { passive: true, capture: true });
    document.addEventListener('mouseover', onPossibleWarmup, { passive: true, capture: true });
    document.addEventListener('focusin', onPossibleWarmup, { capture: true });
  }


  /* ── Kit contents line (2026-08-20) ──────────────────────────────
     Bundle kits have a single variant, so the cart row shows no
     variant info under the title — inject a short "what's inside"
     line instead. Keep texts in sync with the kit bundles in Admin. */
  var KIT_CONTENTS = {
    'essential-cyclist-kit': ['High Contrast Smoke Sharp \u2014 rear-view mirror in the lens', '+ mirror-free Blue lens \u00b7 nosepad \u00b7 neckwear'],
    'performance-cyclist-kit': ['Photochromic 0\u20133 Sharp \u2014 rear-view mirror in the lens', '+ mirror-free Gold lens \u00b7 nosepad \u00b7 neckwear'],
    'essential-rowing-kit': ['High Contrast Smoke Sharp \u2014 dual rear-view mirrors', '+ mirror-free Blue lens \u00b7 nosepad \u00b7 neckwear \u00b7 float retainer'],
    'performance-rowing-kit': ['Photochromic 0\u20133 Sharp \u2014 dual rear-view mirrors', '+ mirror-free Gold lens \u00b7 nosepad \u00b7 neckwear \u00b7 float retainer'],
    'essential-cyclist-kit-prescription': ['High Contrast Smoke Sharp \u2014 rear-view mirror in the lens', '+ mirror-free Blue lens \u00b7 Rx insert \u00b7 neckwear'],
    'performance-cyclist-kit-prescription': ['Photochromic 0\u20133 Sharp \u2014 rear-view mirror in the lens', '+ mirror-free Gold lens \u00b7 Rx insert \u00b7 neckwear'],
    'essential-rowing-kit-prescription': ['High Contrast Smoke Sharp \u2014 dual rear-view mirrors', '+ mirror-free Blue lens \u00b7 Rx insert \u00b7 neckwear \u00b7 float retainer'],
    'performance-rowing-kit-prescription': ['Photochromic 0\u20133 Sharp \u2014 dual rear-view mirrors', '+ mirror-free Gold lens \u00b7 Rx insert \u00b7 neckwear \u00b7 float retainer'],
    'the-classic-kit': ['High Contrast Smoke \u2014 rear-view mirror in the lens', '+ large mirror \u00b7 neckwear'],
    'the-clip-kit': ['Polarized Smoke Sharp clip-on \u2014 rear-view mirror in the lens', '+ neckwear'],
    'reflective-gold-cyclist-kit': ['Reflective Gold Sharp \u2014 rear-view mirror in the lens', '+ mirror-free Gold lens \u00b7 black neckwear'],
    'reflective-gold-cyclist-kit-prescription': ['Reflective Gold Sharp \u2014 rear-view mirror in the lens', '+ mirror-free Gold lens \u00b7 Rx insert \u00b7 black neckwear'],
    'reflective-gold-rowing-kit': ['Reflective Gold Sharp \u2014 dual rear-view mirrors', '+ float retainer \u00b7 black neckwear'],
    'reflective-gold-rowing-kit-prescription': ['Reflective Gold Sharp \u2014 dual rear-view mirrors', '+ float retainer \u00b7 Rx insert \u00b7 black neckwear'],
    'reflective-purple-cyclist-kit': ['Reflective Purple Sharp \u2014 rear-view mirror in the lens', '+ mirror-free Purple lens \u00b7 black neckwear'],
    'reflective-purple-cyclist-kit-prescription': ['Reflective Purple Sharp \u2014 rear-view mirror in the lens', '+ mirror-free Purple lens \u00b7 Rx insert \u00b7 black neckwear'],
    'reflective-purple-rowing-kit': ['Reflective Purple Sharp \u2014 dual rear-view mirrors', '+ float retainer \u00b7 black neckwear'],
    'reflective-purple-rowing-kit-prescription': ['Reflective Purple Sharp \u2014 dual rear-view mirrors', '+ float retainer \u00b7 Rx insert \u00b7 black neckwear'],
    'reflective-blue-cyclist-kit': ['Reflective Blue Sharp \u2014 rear-view mirror in the lens', '+ mirror-free Blue lens \u00b7 black neckwear'],
    'reflective-blue-cyclist-kit-prescription': ['Reflective Blue Sharp \u2014 rear-view mirror in the lens', '+ mirror-free Blue lens \u00b7 Rx insert \u00b7 black neckwear'],
    'polarized-rowing-kit': ['Polarized Smoke Sharp \u2014 dual rear-view mirrors', '+ mirror-free Blue lens \u00b7 nosepad'],
    'polarized-rowing-kit-prescription': ['Polarized Smoke Sharp \u2014 dual rear-view mirrors', '+ mirror-free Blue lens \u00b7 Rx insert'],
    'polarized-cyclist-kit': ['Polarized Smoke Sharp \u2014 rear-view mirror in the lens', '+ mirror-free High Contrast lens \u00b7 nosepad \u00b7 neckwear'],
    'polarized-cyclist-kit-prescription': ['Polarized Smoke Sharp \u2014 rear-view mirror in the lens', '+ mirror-free High Contrast lens \u00b7 Rx insert \u00b7 neckwear']
  };
  function injectKitContents() {
    var rows = document.querySelectorAll('.cart__items__row');
    rows.forEach(function (row) {
      if (row.querySelector('.cart__kit-contents')) return;
      var a = row.querySelector('a[href*="/products/"]');
      if (!a) return;
      var handle = ((a.pathname || '').split('/products/')[1] || '').split('/')[0].split('?')[0];
      var lines = KIT_CONTENTS[handle];
      if (!lines) return;
      var titleCell = a.closest('.cart__items__title') || row.querySelector('.cart__items__title') || a.parentNode;
      var div = document.createElement('div');
      div.className = 'cart__kit-contents';
      div.innerHTML = lines.map(function (l) { return '<span>' + l + '</span>'; }).join('');
      titleCell.appendChild(div);
    });
  }
  function watchKitContents() {
    injectKitContents();
    var host = document.getElementById('drawer-cart') || document.body;
    new MutationObserver(function () { injectKitContents(); }).observe(host, { childList: true, subtree: true });
  }

  // Initial load
  document.addEventListener('DOMContentLoaded', function() {
    setupCartFinalMirror();
    watchKitContents();
    attachWarmup();
    attachLinkWarmup();
    fetch('/cart.js')
      .then(function(r) { return r.json(); })
      .then(onCartChange)
      .catch(function() {});
  });
})();
